import {
  FormControl,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { Text } from "@src/appV2/redesign/components/Text";
import { Title } from "@src/appV2/redesign/components/Title";

import { PricingType, VISIT_TYPE_LABELS, VisitType, WorkerRequirements } from "../types";

export interface Step1Data {
  patientName: string;
  address: string;
  visitType: string;
  workerRequirement: string;
  pricingType: PricingType;
}

interface Props {
  data: Step1Data;
  onChange: (field: keyof Step1Data, value: string | PricingType) => void;
}

const inputSx = {
  bgcolor: "background.paper",
  borderRadius: 2,
  "& .MuiOutlinedInput-root": { borderRadius: 2 },
};

export function MobileCreateCaseStep1({ data, onChange }: Props) {
  return (
    <Stack spacing={3}>
      <Stack spacing={1}>
        <Title component="h5">What&apos;s the patient name?</Title>
        <TextField
          fullWidth
          placeholder="Ilorum ipsum, etc..."
          value={data.patientName}
          onChange={(e) => {
            onChange("patientName", e.target.value);
          }}
          sx={inputSx}
          inputProps={{ "data-dd-privacy": "mask" }}
        />
      </Stack>

      <Stack spacing={1}>
        <Title component="h5">What&apos;s the address?</Title>
        <TextField
          fullWidth
          placeholder="Ilorum ipsum, etc..."
          value={data.address}
          onChange={(e) => {
            onChange("address", e.target.value);
          }}
          sx={inputSx}
          inputProps={{ "data-dd-privacy": "mask" }}
        />
      </Stack>

      <Stack spacing={1}>
        <Title component="h5">What kind of visits?</Title>
        <FormControl fullWidth sx={inputSx}>
          <Select
            displayEmpty
            value={data.visitType}
            onChange={(e) => {
              onChange("visitType", e.target.value);
            }}
            renderValue={(value) =>
              value ? VISIT_TYPE_LABELS[value as VisitType] : "Select an option"
            }
          >
            {Object.entries(VISIT_TYPE_LABELS).map(([type, label]) => (
              <MenuItem key={type} value={type}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      <Stack spacing={1}>
        <Title component="h5">What kind of professional?</Title>
        <FormControl fullWidth sx={inputSx}>
          <Select
            displayEmpty
            value={data.workerRequirement}
            onChange={(e) => {
              onChange("workerRequirement", e.target.value);
            }}
            renderValue={(value) => (value ? (value as string) : "Select an option")}
          >
            {Object.values(WorkerRequirements).map((value) => (
              <MenuItem key={value} value={value}>
                {value}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      <Stack spacing={0.5}>
        <Title component="h5">Type</Title>
        <Text variant="body2" sx={{ color: "text.secondary", fontStyle: "italic" }}>
          Paid per visit or per hour
        </Text>
        <ToggleButtonGroup
          value={data.pricingType}
          exclusive
          onChange={(_, newValue: PricingType | null) => {
            if (newValue !== null) {
              onChange("pricingType", newValue);
            }
          }}
          sx={{ mt: 1 }}
        >
          <ToggleButton value={PricingType.PER_VISIT}>Per visit</ToggleButton>
          <ToggleButton value={PricingType.PER_HOUR}>Per hour</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
    </Stack>
  );
}
